// Flat question data model for the WLC Right-to-Choose ADHD referral screener.
//
// Every screen the user sees maps to one entry in this array.  The orchestrator
// component iterates through them using `currentQuestionIndex`.
//
// The `type` field determines which input renderer the orchestrator uses.
// The `phase` field is used for progress-bar logic and scoring.

import {
    ASRS_RATING_OPTIONS,
    IMPAIRMENT_LIKERT_OPTIONS,
    YES_NO_OPTIONS,
} from './options';

// ─── Phase 1: ASRS Part A (screens 0-5) ─────────────────────────────────────

const asrsQuestions = [
    {
        id: 'asrs_1',
        phase: 'asrs',
        question: 'How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?',
        type: 'rating',
        required: true,
        options: ASRS_RATING_OPTIONS,
    },
    {
        id: 'asrs_2',
        phase: 'asrs',
        question: 'How often do you have difficulty getting things in order when you have a task that requires organisation?',
        type: 'rating',
        required: true,
        options: ASRS_RATING_OPTIONS,
    },
    {
        id: 'asrs_3',
        phase: 'asrs',
        question: 'How often do you have problems remembering appointments or obligations?',
        type: 'rating',
        required: true,
        options: ASRS_RATING_OPTIONS,
    },
    {
        id: 'asrs_4',
        phase: 'asrs',
        question: 'When you have a task that requires a lot of thought, how often do you avoid or delay getting started?',
        type: 'rating',
        required: true,
        options: ASRS_RATING_OPTIONS,
    },
    {
        id: 'asrs_5',
        phase: 'asrs',
        question: 'How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?',
        type: 'rating',
        required: true,
        options: ASRS_RATING_OPTIONS,
    },
    {
        id: 'asrs_6',
        phase: 'asrs',
        question: 'How often do you feel overly active and compelled to do things, like you were driven by a motor?',
        type: 'rating',
        required: true,
        options: ASRS_RATING_OPTIONS,
    },
];

// ─── Phase 3: Functional Impairment (screens 6-11) ──────────────────────────

const impairmentQuestions = [
    {
        id: 'impairment_work',
        phase: 'impairment',
        question: 'How much do your difficulties affect your work or studies?',
        subtitle: 'e.g. finishing tasks, meeting deadlines, staying focused',
        type: 'likert',
        required: true,
        options: IMPAIRMENT_LIKERT_OPTIONS,
    },
    {
        id: 'impairment_organisation',
        phase: 'impairment',
        question: 'How much do your difficulties affect your organisation & time management?',
        subtitle: 'e.g. planning, losing things, being late',
        type: 'likert',
        required: true,
        options: IMPAIRMENT_LIKERT_OPTIONS,
    },
    {
        id: 'impairment_relationships',
        phase: 'impairment',
        question: 'How much do your difficulties affect your relationships?',
        subtitle: 'e.g. with family, friends, or colleagues',
        type: 'likert',
        required: true,
        options: IMPAIRMENT_LIKERT_OPTIONS,
    },
    {
        id: 'impairment_emotions',
        phase: 'impairment',
        question: 'How much do your difficulties affect your emotions?',
        subtitle: 'e.g. mood swings, frustration, feeling overwhelmed',
        type: 'likert',
        required: true,
        options: IMPAIRMENT_LIKERT_OPTIONS,
    },
    {
        id: 'impairment_daily',
        phase: 'impairment',
        question: 'How much do your difficulties affect your daily life?',
        subtitle: 'e.g. finances, household tasks, appointments',
        type: 'likert',
        required: true,
        options: IMPAIRMENT_LIKERT_OPTIONS,
    },
    {
        id: 'impairment_sleep',
        phase: 'impairment',
        question: 'How much do your difficulties affect your sleep?',
        type: 'likert',
        required: true,
        options: IMPAIRMENT_LIKERT_OPTIONS,
    },
];

// ─── Phase 4: Personal Details (screens 12-19) ──────────────────────────────

const personalQuestions = [
    {
        id: 'names',
        phase: 'personal',
        question: 'What is your name?',
        instruction: 'Please provide your name so we can include it in your referral letter.',
        type: 'names',
        required: true,
        fields: [
            { id: 'firstName', label: 'First name', type: 'text', required: true },
            { id: 'middleName', label: 'Middle name (optional)', type: 'text', required: false },
            { id: 'lastName', label: 'Last name', type: 'text', required: true },
        ],
    },
    {
        id: 'dob',
        phase: 'personal',
        question: 'What is your date of birth?',
        instruction: 'Please select your date of birth.',
        type: 'dob',
        required: true,
    },
    {
        id: 'nhsNumber',
        phase: 'personal',
        question: 'What is your NHS number?',
        instruction: 'This is optional — if you don\'t know it, you can skip this question.',
        nudgeText: 'You can find this on any NHS letter, or by calling your GP surgery.',
        type: 'nhsNumber',
        required: false,
    },
    {
        id: 'address',
        phase: 'personal',
        question: 'What is your address?',
        type: 'address',
        required: true,
    },
    {
        id: 'phone',
        phase: 'personal',
        question: 'What is your phone number?',
        instruction: 'We\'ll use this to contact you about your referral if needed.',
        type: 'phone',
        required: true,
    },
    {
        id: 'emailConfirm',
        phase: 'personal',
        question: 'What is your email address?',
        instruction: 'We\'ll send your GP letter and referral confirmation to this address.',
        type: 'emailConfirm',
        required: true,
    },
];

// ─── Phase 5: Previous Assessment (screens 20-22) ───────────────────────────

const previousAssessmentQuestions = [
    {
        id: 'assessedBefore',
        phase: 'previousAssessment',
        question: 'Have you been assessed for ADHD before?',
        type: 'yesNo',
        required: true,
        options: YES_NO_OPTIONS,
        conditionalInfo: 'Please bring any letters or reports to your GP appointment.',
        conditionalTrigger: 'Yes',
    },
    {
        id: 'existingDiagnosis',
        phase: 'previousAssessment',
        question: 'Do you have an existing ADHD diagnosis?',
        type: 'yesNo',
        required: true,
        options: YES_NO_OPTIONS,
        conditionalInfo: 'Your GP will need to include the report with this referral.',
        conditionalTrigger: 'Yes',
    },
    {
        id: 'previousMedication',
        phase: 'previousAssessment',
        question: 'Have you taken ADHD medication before?',
        type: 'yesNo',
        required: true,
        options: YES_NO_OPTIONS,
        conditionalTrigger: 'Yes',
        conditionalInput: {
            id: 'medicationDetails',
            placeholder: 'Please give brief details — what it was and whether it helped.',
            required: true,
        },
    },
];

// ─── Combined flat array (screens 0-22) ─────────────────────────────────────

export const ALL_QUESTIONS = [
    ...asrsQuestions,
    ...impairmentQuestions,
    ...personalQuestions,
    ...previousAssessmentQuestions,
];

// Convenience counts used by progress and section calculations
export const ASRS_COUNT = asrsQuestions.length;           // 6
export const IMPAIRMENT_COUNT = impairmentQuestions.length; // 6
export const PERSONAL_COUNT = personalQuestions.length;     // 6
export const PREV_ASSESSMENT_COUNT = previousAssessmentQuestions.length; // 3
